// DL3 — the node-client download page, INSIDE the dashboard shell.
//
// It began life as a server-rendered page on the gateway (DL1): its own HTML, its own dark card,
// its own "Already have an account? Sign in" footer — because it had to stand alone for a visitor
// with no account. That is no longer who reaches it. The way in is the Nodes tab's button, so the
// page is a signed-in surface like every other tab: same sidebar, same tokens, same layout, and the
// gate is the one the shell already applies (an anonymous visitor to /download is sent to the
// authorization server's login page by <Gate/>, exactly as for /clients).
//
// What still belongs to the gateway is the DATA and the BYTES: `/download/artifacts.json` (the
// manifest an operator publishes by dropping files into a bind mount) and `/download/artifacts/*`
// (streamed, resumable). Both are reached through this dashboard's nginx on the same origin, so
// nothing here knows the gateway's address.

import { useState } from 'react';
import { useResource } from '../lib/useResource';
import { Card, Loading, ErrorState, bytesHuman } from '../components/ui';

/** The SPA route this view is mounted at, and what the Nodes tab links to. */
export const DOWNLOAD_ROUTE = '/download';

/** Where the manifest and the bytes live — same origin, proxied to the gateway by nginx. */
export const MANIFEST_URL = '/download/artifacts.json';
export const artifactUrl = (file: string) => `/download/artifacts/${encodeURIComponent(file)}`;

export interface ManifestEntry {
  file: string;
  os?: string;
  label?: string;
  size?: number;
  sha256?: string;
}

export interface Manifest {
  version?: string;
  updated?: string;
  files?: ManifestEntry[];
}

export interface Artifact {
  file: string;
  label: string;
  size: number;
  sha256: string;
}

export interface OsGroup {
  id: string;
  name: string;
  blurb: string;
  icon: string;
  artifacts: Artifact[];
}

// Which card an entry belongs to when the manifest does not say. Extension is the honest signal —
// a release's file names churn (`_x64-setup.exe` → `_x86_64-setup.exe`) far more often than the
// extension does. Mirrors the gateway's own `_OS_BY_EXT`.
const OS_BY_EXT: Record<string, string> = {
  exe: 'windows',
  msi: 'windows',
  deb: 'linux',
  appimage: 'linux',
};

// 24×24 glyphs, drawn as FILLED paths so they read at card size. Windows: the four-pane logo in its
// modern (flat, not perspective) form. Linux: a plain penguin silhouette.
const WINDOWS_ICON = 'M3 5.6l7.2-1v6.7H3zM11.6 4.4L21 3v8.3h-9.4zM3 12.7h7.2v6.7L3 18.4zM11.6 12.7H21V21l-9.4-1.3z';
const LINUX_ICON =
  'M12 2c2.3 0 3.7 1.9 3.7 4.5 0 1.6.4 2.4 1.3 3.7 1.2 1.7 2.2 3 2.5 4.8.3 1.6-.2 2.8-1.3 3.4-.7.4-1.4.3-1.9-.1-.4 1-1.9 1.7-4.3 1.7s-3.9-.7-4.3-1.7c-.5.4-1.2.5-1.9.1-1.1-.6-1.6-1.8-1.3-3.4.3-1.8 1.3-3.1 2.5-4.8.9-1.3 1.3-2.1 1.3-3.7C8.3 3.9 9.7 2 12 2zm-1.6 4.1a.9 1.1 0 100 2.2.9 1.1 0 000-2.2zm3.2 0a.9 1.1 0 100 2.2.9 1.1 0 000-2.2zM12 9.2c-.9 0-1.7.4-1.7.9s.8 1.1 1.7 1.1 1.7-.6 1.7-1.1-.8-.9-1.7-.9z';

const OS_CARDS: { id: string; name: string; blurb: string; icon: string }[] = [
  {
    id: 'windows',
    name: 'Windows',
    blurb: 'Windows 10/11, x64. The installer bundles the node itself — no Python and no Docker to install first.',
    icon: WINDOWS_ICON,
  },
  {
    id: 'linux',
    name: 'Linux',
    blurb: 'x86_64. Install the .deb on Debian/Ubuntu, or run the AppImage directly.',
    icon: LINUX_ICON,
  },
];

function extOf(file: string): string {
  const i = file.lastIndexOf('.');
  return i < 0 ? '' : file.slice(i + 1).toLowerCase();
}

/**
 * Manifest → the two OS cards, in a fixed order.
 *
 * A card with no artifacts is KEPT (rendered "coming soon") rather than dropped: Linux sits in
 * exactly that state until a release produces the .deb/AppImage, and a page that silently loses a
 * platform reads as a bug. An entry with no usable file name is dropped, and one with no size falls
 * back to 0 (rendered as an unknown size, never as "0 B" pretending to be a fact).
 */
export function groupArtifacts(manifest: Manifest | null): OsGroup[] {
  const files = Array.isArray(manifest?.files) ? manifest!.files! : [];
  return OS_CARDS.map((card) => ({
    ...card,
    artifacts: files
      .filter((f): f is ManifestEntry => !!f && typeof f.file === 'string' && f.file.length > 0)
      .filter((f) => (f.os || OS_BY_EXT[extOf(f.file)] || '') === card.id)
      .map((f) => ({
        file: f.file,
        label: f.label || extOf(f.file).toUpperCase() || f.file,
        size: typeof f.size === 'number' && f.size > 0 ? f.size : 0,
        sha256: typeof f.sha256 === 'string' ? f.sha256 : '',
      })),
  }));
}

/** The manifest's `updated` stamp as a person reads it. Falls back to the raw string when it is not
 *  a date we understand — an odd value is still a fact worth showing, just not one to reformat. */
export function formatUpdated(raw?: string): string {
  if (!raw) return '';
  const t = Date.parse(raw);
  if (Number.isNaN(t)) return raw;
  return new Date(t).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** A sha256 as shown on screen: enough to recognise, not enough to wrap. The full value is what
 *  the copy button puts on the clipboard. */
export function shortSha(sha: string): string {
  return sha ? `${sha.slice(0, 12)}…` : '';
}

function CopySha({ sha }: { sha: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="dl-sha"
      title="Copy the full SHA-256"
      data-testid="copy-sha"
      onClick={() => {
        navigator.clipboard?.writeText(sha);
        setDone(true);
        window.setTimeout(() => setDone(false), 1400);
      }}
    >
      <code>{done ? 'copied' : shortSha(sha)}</code>
    </button>
  );
}

export function OsCard({ group }: { group: OsGroup }) {
  const empty = group.artifacts.length === 0;
  return (
    <section
      className={`card dl-os${empty ? ' is-empty' : ''}`}
      data-testid={`download-card-${group.id}`}
      data-os={group.id}
    >
      <div className="card-body">
        <div className="dl-os-head">
          <svg className="dl-os-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d={group.icon} fill="currentColor" />
          </svg>
          <div>
            <h2>{group.name}</h2>
            <p className="small muted dl-os-blurb">{group.blurb}</p>
          </div>
        </div>

        {empty ? (
          <div className="dl-soon">Coming soon</div>
        ) : (
          <div className="dl-rows">
            {group.artifacts.map((a) => (
              <div className="dl-row" key={a.file}>
                <div className="dl-meta">
                  <div className="dl-name">{a.label}</div>
                  <div className="dl-facts small muted">
                    <span>{a.size ? bytesHuman(a.size) : 'size unknown'}</span>
                    {a.sha256 && <CopySha sha={a.sha256} />}
                  </div>
                </div>
                <a className="btn btn-primary" href={artifactUrl(a.file)} data-testid="download-link" download>
                  Download
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function Download() {
  const res = useResource<Manifest>(
    () =>
      fetch(MANIFEST_URL, { credentials: 'same-origin' }).then((r) => {
        if (!r.ok) throw new Error(`manifest: HTTP ${r.status}`);
        return r.json() as Promise<Manifest>;
      }),
    [],
  );
  const manifest = res.data;
  const groups = groupArtifacts(manifest);
  const published = groups.some((g) => g.artifacts.length > 0);

  return (
    <div className="view">
      <div className="view-head">
        <h1>Node client</h1>
      </div>

      {res.loading ? (
        <Loading what="Loading published builds…" />
      ) : res.error ? (
        <ErrorState message={res.error} onRetry={res.reload} />
      ) : (
        <>
          <section className="card dl-hero">
            <div className="card-body dl-hero-body">
              <div>
                <div className="dl-version" data-testid="download-version">
                  {manifest?.version ? `v${manifest.version}` : 'unreleased'}
                </div>
                {manifest?.updated && (
                  <div className="small muted">published {formatUpdated(manifest.updated)}</div>
                )}
              </div>
              <p className="small muted dl-hero-text">
                The node client runs on YOUR machine and joins it to this gateway: it registers the node,
                pulls the service images it is asked to run, and reports GPU/VRAM capacity. The node
                dashboard is built in, so after signing in there is nothing else to install.
              </p>
            </div>
          </section>

          <div className="dl-grid">
            {groups.map((g) => (
              <OsCard key={g.id} group={g} />
            ))}
          </div>

          {!published && (
            <Card collapsible={false}>
              <div className="empty">
                No builds published yet. An operator publishes one by dropping the installers into the
                gateway's artifacts mount.
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
